import firebase from '../config/firebase';

const PLATFORM_COLLECTION = 'social_platform_configs';
const POST_COLLECTION = 'vendor_social_posts';
const LOCAL_SOCIAL_CONFIGS_KEY = 'socialPlatformConfigs';
const LOCAL_VENDOR_POSTS_PREFIX = 'vendorSocialPosts:';
const FIREBASE_STORAGE_BASE =
  'https://firebasestorage.googleapis.com/v0/b/go-together-6b9cc.appspot.com/o/';
const CLOUD_FUNCTIONS_BASE =
  'https://us-central1-go-together-6b9cc.cloudfunctions.net';
const FACEBOOK_PUBLISH_ENDPOINT = `${CLOUD_FUNCTIONS_BASE}/publishFacebook`;

function getLocalStorageValue(key) {
  try {
    return localStorage.getItem(key);
  } catch (error) {
    return null;
  }
}

function setLocalStorageValue(key, value) {
  try {
    localStorage.setItem(key, value);
  } catch (error) {
    // localStorage unavailable
  }
}

function getLocalJson(key, fallback) {
  const raw = getLocalStorageValue(key);
  if (!raw) {
    return fallback;
  }
  try {
    return JSON.parse(raw);
  } catch (error) {
    return fallback;
  }
}

function setLocalJson(key, value) {
  setLocalStorageValue(key, JSON.stringify(value));
}

function getVendorPostsStorageKey(vendorId) {
  return `${LOCAL_VENDOR_POSTS_PREFIX}${vendorId}`;
}

function getAuthToken() {
  try {
    if (!firebase.auth || typeof firebase.auth !== 'function') {
      return Promise.resolve(null);
    }

    const auth = firebase.auth();
    if (
      !auth ||
      !auth.currentUser ||
      typeof auth.currentUser.getIdToken !== 'function'
    ) {
      return Promise.resolve(null);
    }

    return auth.currentUser.getIdToken();
  } catch (error) {
    return Promise.resolve(null);
  }
}

function createSocialPublishError(message, code, status) {
  const error = new Error(message);
  if (code) {
    error.code = code;
  }
  if (status !== undefined) {
    error.status = status;
  }
  return error;
}

const defaultPlatformConfigs = [
  {
    id: 'facebook',
    name: 'Facebook',
    active: true,
    order: 1,
    supportsScheduling: true,
    scheduleMinLeadMinutes: 10,
    scheduleMaxLeadDays: 30,
    textMaxChars: 63206,
    mediaMaxItems: 10,
    imageMaxMB: 25,
    videoMaxMB: 1024,
    imageWidth: 1200,
    imageHeight: 627,
    notes: 'Pages API; scheduling window 10 minutes to 30 days.',
    sourceConfidence: 'official',
  },
  {
    id: 'instagram',
    name: 'Instagram',
    active: true,
    order: 2,
    supportsScheduling: true,
    textMaxChars: 2200,
    mediaMaxItems: 10,
    imageMaxMB: 8,
    imageWidth: 1080,
    imageHeight: 1080,
    videoMaxMB: 1024,
    allowedImageFormats: ['image/jpeg'],
    apiPublishRateLimit: '100-per-24h',
    notes:
      'Graph API publishing; JPEG image support and up to 10 carousel items.',
    sourceConfidence: 'official',
  },
  {
    id: 'tiktok',
    name: 'TickTock',
    active: true,
    order: 3,
    supportsScheduling: false,
    textMaxChars: 2200,
    mediaMaxItems: 1,
    imageMaxMB: 20,
    imageWidth: 1080,
    imageHeight: 1920,
    videoMaxMB: 1024,
    allowedVideoFormats: [
      'video/mp4',
      'video/quicktime',
      'video/webm',
    ],
    notes:
      'Upload is init/append/finalize; capabilities vary by app access.',
    sourceConfidence: 'official-partial',
  },
  {
    id: 'youtube',
    name: 'YouTube',
    active: true,
    order: 4,
    supportsScheduling: true,
    textMaxChars: 5000,
    mediaMaxItems: 1,
    imageMaxMB: 2,
    videoMaxMB: 262144,
    imageWidth: 1280,
    imageHeight: 720,
    maxVideoDurationHours: 12,
    notes: 'Upload max is 256GB or 12 hours.',
    sourceConfidence: 'official',
  },
  {
    id: 'snapchat',
    name: 'Snapchat',
    active: true,
    order: 5,
    supportsScheduling: false,
    textMaxChars: 250,
    mediaMaxItems: 1,
    imageMaxMB: 32,
    imageWidth: 1080,
    imageHeight: 1920,
    videoMaxMB: 1024,
    notes:
      'Creative constraints vary by product. Validate per connected API.',
    sourceConfidence: 'official-partial',
  },
  {
    id: 'x',
    name: 'Twitter',
    active: true,
    order: 6,
    supportsScheduling: false,
    textMaxChars: 280,
    mediaMaxItems: 4,
    imageMaxMB: 5,
    gifMaxMB: 15,
    videoMaxMB: 512,
    imageWidth: 1200,
    imageHeight: 675,
    taggedUsersMax: 10,
    notes: 'Media limits from API docs and OpenAPI schema.',
    sourceConfidence: 'official',
  },
];

function sortByOrder(items) {
  return items.sort((a, b) => {
    const first = a.order !== undefined ? a.order : 999;
    const second = b.order !== undefined ? b.order : 999;
    return first - second;
  });
}

function normalizePlatformId(value) {
  if (!value || typeof value !== 'string') {
    return '';
  }

  const normalized = value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]/g, '');

  if (normalized === 'twitter') {
    return 'x';
  }
  if (normalized === 'xtwitter') {
    return 'x';
  }
  if (normalized === 'tiktok' || normalized === 'ticktock') {
    return 'tiktok';
  }

  return normalized;
}

function removeUndefinedFields(payload) {
  const result = {};
  Object.keys(payload || {}).forEach((key) => {
    if (payload[key] !== undefined) {
      result[key] = payload[key];
    }
  });
  return result;
}

function getMergedPlatformConfigs(existingConfigs) {
  const existingById = {};
  (existingConfigs || []).forEach((config) => {
    const id =
      normalizePlatformId(config.id) ||
      normalizePlatformId(config.name);
    if (id) {
      existingById[id] = {
        ...config,
        id,
      };
    }
  });

  const mergedDefaults = defaultPlatformConfigs.map((platform) => {
    const existing = existingById[platform.id] || {};
    return {
      ...platform,
      ...existing,
      id: platform.id,
      active:
        existing.active !== undefined
          ? existing.active
          : platform.active,
    };
  });

  const defaultIds = defaultPlatformConfigs.map((item) => item.id);
  const extras = (existingConfigs || []).filter((item) => {
    const id =
      normalizePlatformId(item.id) || normalizePlatformId(item.name);
    return id && !defaultIds.includes(id);
  });

  const extraNormalized = extras.map((item, index) => ({
    ...item,
    id:
      normalizePlatformId(item.id) || normalizePlatformId(item.name),
    active: item.active !== undefined ? item.active : true,
    order:
      item.order !== undefined
        ? item.order
        : defaultPlatformConfigs.length + index + 1,
  }));

  return sortByOrder([...mergedDefaults, ...extraNormalized]);
}

function buildStorageUrl(path, folder) {
  if (!path || typeof path !== 'string') {
    return null;
  }

  if (
    path.indexOf('http://') === 0 ||
    path.indexOf('https://') === 0
  ) {
    return path;
  }

  const normalizedPath = folder ? `${folder}/${path}` : path;
  return `${FIREBASE_STORAGE_BASE}${encodeURIComponent(
    normalizedPath,
  )}?alt=media`;
}

function extractImageValue(image) {
  if (!image) {
    return null;
  }

  if (typeof image === 'string') {
    return image;
  }

  if (Array.isArray(image)) {
    for (let i = 0; i < image.length; i += 1) {
      const found = extractImageValue(image[i]);
      if (found) {
        return found;
      }
    }
    return null;
  }

  if (typeof image === 'object') {
    return (
      image.url ||
      image.src ||
      image.path ||
      image.imageUrl ||
      image.downloadURL ||
      image.heroPath ||
      image.thumbPath ||
      image.original ||
      null
    );
  }

  return null;
}

function getPromoImage(data) {
  return extractImageValue(
    data.images ||
      data.image ||
      data.heroImage ||
      data.thumbnail ||
      data.promoImage,
  );
}

function getEventImage(data) {
  const rawImage =
    extractImageValue(
      data.thumbnail ||
        data.heroImage ||
        data.eventThumb ||
        data.eventHero ||
        data.image,
    ) || null;

  if (!rawImage) {
    return null;
  }

  if (
    rawImage.indexOf('http://') === 0 ||
    rawImage.indexOf('https://') === 0
  ) {
    return rawImage;
  }

  if (rawImage === data.eventThumb || rawImage === data.eventHero) {
    return buildStorageUrl(rawImage, 'location_images');
  }

  return buildStorageUrl(rawImage);
}

function getLocationImage(data) {
  const rawImage =
    extractImageValue(
      data.locationThumb ||
        data.locationHero ||
        data.thumbnail ||
        data.heroImage ||
        data.image,
    ) || null;

  if (!rawImage) {
    return null;
  }

  if (
    rawImage.indexOf('http://') === 0 ||
    rawImage.indexOf('https://') === 0
  ) {
    return rawImage;
  }

  if (
    rawImage === data.locationThumb ||
    rawImage === data.locationHero
  ) {
    return buildStorageUrl(rawImage, 'location_images');
  }

  return buildStorageUrl(rawImage);
}

export function getDefaultPlatformConfigs() {
  return defaultPlatformConfigs;
}

export function getSocialPlatformConfigs() {
  const cached = getLocalJson(LOCAL_SOCIAL_CONFIGS_KEY, null);
  if (Array.isArray(cached) && cached.length > 0) {
    return Promise.resolve(getMergedPlatformConfigs(cached));
  }

  const db = firebase.firestore();

  return new Promise((resolve, reject) => {
    db.collection(PLATFORM_COLLECTION)
      .get()
      .then((snapshot) => {
        const configs = [];
        snapshot.forEach((doc) => {
          if (doc && doc.exists) {
            configs.push({
              ...doc.data(),
              id: normalizePlatformId(doc.id),
            });
          }
        });

        const merged = getMergedPlatformConfigs(configs);

        const existingIds = configs.map((item) => item.id);
        const missingDefaults = defaultPlatformConfigs.filter(
          (platform) => !existingIds.includes(platform.id),
        );

        if (missingDefaults.length > 0) {
          // const batch = db.batch();
          // missingDefaults.forEach((platform) => {
          //   const ref = db
          //     .collection(PLATFORM_COLLECTION)
          //     .doc(platform.id);
          //   batch.set(ref, {
          //     ...platform,
          //     createdAt:
          //       firebase.firestore.FieldValue.serverTimestamp(),
          //     updatedAt:
          //       firebase.firestore.FieldValue.serverTimestamp(),
          //   });
          // });

          console.log(
            'Auto-seed social platform defaults (DB write skipped)',
            missingDefaults,
          );
          setLocalJson(LOCAL_SOCIAL_CONFIGS_KEY, merged);
          resolve(merged);

          // batch
          //   .commit()
          //   .then(() => {
          //     resolve(merged);
          //   })
          //   .catch((error) => {
          //     reject(error);
          //   });
        } else {
          setLocalJson(LOCAL_SOCIAL_CONFIGS_KEY, merged);
          resolve(merged);
        }
      })
      .catch((error) => {
        const fallback = getMergedPlatformConfigs(
          defaultPlatformConfigs,
        );
        setLocalJson(LOCAL_SOCIAL_CONFIGS_KEY, fallback);
        console.log(
          'Load social platform configs from defaults (DB read failed)',
          error,
        );
        resolve(fallback);
      });
  });
}

export function saveSocialPlatformConfigs(configs, updatedBy) {
  return new Promise((resolve) => {
    const normalizedInput = getMergedPlatformConfigs(configs || []);

    normalizedInput.forEach((platform, index) => {
      const platformId =
        normalizePlatformId(platform.id) ||
        normalizePlatformId(platform.name);
      if (!platformId) {
        return;
      }
      // const ref = db.collection(PLATFORM_COLLECTION).doc(platformId);

      const payload = removeUndefinedFields({
        ...platform,
        id: platformId,
        order:
          platform.order !== undefined ? platform.order : index + 1,
        active:
          platform.active !== undefined ? platform.active : true,
        updatedBy: updatedBy || null,
        updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
      });

      // batch.set(
      //   ref,
      //   payload,
      //   { merge: true },
      // );
      console.log('Save social platform config (DB write skipped)', {
        platformId,
        payload,
      });
    });

    const saved = sortByOrder([...normalizedInput]);
    setLocalJson(LOCAL_SOCIAL_CONFIGS_KEY, saved);
    resolve(saved);

    // batch
    //   .commit()
    //   .then(() => {
    //     resolve(sortByOrder([...normalizedInput]));
    //   })
    //   .catch((error) => {
    //     reject(error);
    //   });
  });
}

export function getVendorSocialPosts(vendorId) {
  const localPosts = getLocalJson(
    getVendorPostsStorageKey(vendorId),
    [],
  );
  if (Array.isArray(localPosts) && localPosts.length > 0) {
    const normalized = [...localPosts].sort((a, b) => {
      const aDate =
        a.createdAt && a.createdAt.seconds ? a.createdAt.seconds : 0;
      const bDate =
        b.createdAt && b.createdAt.seconds ? b.createdAt.seconds : 0;
      return bDate - aDate;
    });
    return Promise.resolve(normalized);
  }

  const db = firebase.firestore();

  return new Promise((resolve, reject) => {
    db.collection(POST_COLLECTION)
      .where('vendorId', '==', vendorId)
      .get()
      .then((snapshot) => {
        const posts = [];
        snapshot.forEach((doc) => {
          if (doc && doc.exists) {
            const payload = doc.data();
            posts.push({
              id: doc.id,
              ...payload,
            });
          }
        });

        posts.sort((a, b) => {
          const aDate =
            a.createdAt && a.createdAt.seconds
              ? a.createdAt.seconds
              : 0;
          const bDate =
            b.createdAt && b.createdAt.seconds
              ? b.createdAt.seconds
              : 0;
          return bDate - aDate;
        });

        resolve(posts);
      })
      .catch((error) => {
        reject(error);
      });
  });
}

export function createVendorSocialPost(bundle) {
  return new Promise((resolve) => {
    const nextId = `mock-${Date.now()}`;
    const nowSeconds = Math.floor(Date.now() / 1000);
    const nextPost = {
      id: nextId,
      ...bundle,
      status: bundle.status || 'draft',
      createdAt: { seconds: nowSeconds },
      updatedAt: { seconds: nowSeconds },
    };
    const key = getVendorPostsStorageKey(bundle.vendorId);
    const current = getLocalJson(key, []);
    const updated = Array.isArray(current)
      ? [nextPost, ...current]
      : [nextPost];

    setLocalJson(key, updated);
    console.log(
      'Create vendor social post (saved to localStorage)',
      nextPost,
    );
    resolve(nextId);

    // db.collection(POST_COLLECTION)
    //   .add({
    //     ...bundle,
    //     status: bundle.status || 'draft',
    //     createdAt: firebase.firestore.FieldValue.serverTimestamp(),
    //     updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
    //   })
    //   .then((docRef) => {
    //     resolve(docRef.id);
    //   })
    //   .catch((error) => {
    //     reject(error);
    //   });
  });
}

export function updateVendorSocialPost(bundle) {
  return new Promise((resolve, reject) => {
    if (!bundle || !bundle.vendorId || !bundle.id) {
      reject(new Error('vendorId and id are required for update.'));
      return;
    }

    const key = getVendorPostsStorageKey(bundle.vendorId);
    const current = getLocalJson(key, []);
    if (!Array.isArray(current)) {
      reject(new Error('Invalid local post storage format.'));
      return;
    }

    const nowSeconds = Math.floor(Date.now() / 1000);
    const next = current.map((post) => {
      if (post.id !== bundle.id) {
        return post;
      }
      return {
        ...post,
        ...bundle,
        updatedAt: { seconds: nowSeconds },
      };
    });

    setLocalJson(key, next);
    console.log('Update vendor social post (saved to localStorage)', {
      postId: bundle.id,
      payload: bundle,
    });
    resolve(bundle.id);
  });
}

export function pushVendorSocialPostNow(bundle) {
  return new Promise((resolve, reject) => {
    if (!bundle || !bundle.vendorId) {
      reject(new Error('vendorId is required to push a post.'));
      return;
    }

    const normalizedPlatforms = (bundle.selectedPlatforms || [])
      .map((platformId) => normalizePlatformId(platformId))
      .filter((platformId) => platformId);

    if (!normalizedPlatforms.includes('facebook')) {
      reject(
        new Error(
          'Facebook must be selected for backend publish in this flow.',
        ),
      );
      return;
    }

    const key = getVendorPostsStorageKey(bundle.vendorId);
    const current = getLocalJson(key, []);
    if (!Array.isArray(current)) {
      reject(new Error('Invalid local post storage format.'));
      return;
    }

    const nowSeconds = Math.floor(Date.now() / 1000);
    const platformPayloads = (bundle.selectedPlatforms || []).map(
      (platformId) => {
        const override =
          (bundle.platformOverrides || {})[platformId] || {};
        return {
          platformId,
          text: override.textOverride || bundle.postText || '',
          imageUrl:
            override.imageUrl ||
            (bundle.sourceContent && bundle.sourceContent.image) ||
            null,
          crop: {
            scale:
              override.cropScale !== undefined
                ? override.cropScale
                : 1,
            x: override.cropX !== undefined ? override.cropX : 0,
            y: override.cropY !== undefined ? override.cropY : 0,
            confirmed: override.cropConfirmed === true,
          },
        };
      },
    );

    const payload = {
      ...bundle,
      selectedPlatforms: normalizedPlatforms,
    };

    getAuthToken()
      .then((idToken) => {
        if (!idToken) {
          throw createSocialPublishError(
            'User must be authenticated before publishing to Facebook.',
            'unauthenticated',
            401,
          );
        }

        return fetch(FACEBOOK_PUBLISH_ENDPOINT, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${idToken}`,
          },
          body: JSON.stringify(payload),
        });
      })
      .then((response) => {
        return response
          .json()
          .catch(() => ({}))
          .then((body) => ({
            ok: response.ok,
            status: response.status,
            body,
          }));
      })
      .then((result) => {
        if (!result.ok) {
          const backendCode =
            result.body && result.body.code
              ? result.body.code
              : 'facebook-publish-failed';
          const backendMessage =
            result.body && result.body.message
              ? result.body.message
              : `Facebook publish failed with status ${result.status}.`;
          throw createSocialPublishError(
            backendMessage,
            backendCode,
            result.status,
          );
        }

        console.log(
          'Push to social media (backend publish succeeded)',
          {
            vendorId: bundle.vendorId,
            postId: bundle.id || null,
            facebookPostId:
              result.body && result.body.facebookPostId
                ? result.body.facebookPostId
                : null,
            platformPayloads,
          },
        );

        const postId = bundle.id || `mock-${Date.now()}`;
        let found = false;
        const next = current.map((post) => {
          if (post.id !== postId) {
            return post;
          }
          found = true;
          return {
            ...post,
            ...bundle,
            id: postId,
            status: 'published',
            pushedAt: { seconds: nowSeconds },
            updatedAt: { seconds: nowSeconds },
            platformPublishMeta: {
              facebookPostId:
                result.body && result.body.facebookPostId
                  ? result.body.facebookPostId
                  : null,
            },
          };
        });

        if (!found) {
          next.unshift({
            ...bundle,
            id: postId,
            status: 'published',
            pushedAt: { seconds: nowSeconds },
            createdAt: { seconds: nowSeconds },
            updatedAt: { seconds: nowSeconds },
            platformPublishMeta: {
              facebookPostId:
                result.body && result.body.facebookPostId
                  ? result.body.facebookPostId
                  : null,
            },
          });
        }

        setLocalJson(key, next);
        resolve(postId);
      })
      .catch((error) => {
        if (error && error.message === 'Failed to fetch') {
          reject(
            createSocialPublishError(
              'Network error reaching Facebook publish service.',
              'publish-service-unreachable',
              503,
            ),
          );
          return;
        }

        reject(error);
      });
  });
}

export function deleteVendorSocialPost(postId) {
  return new Promise((resolve) => {
    try {
      for (let i = 0; i < localStorage.length; i += 1) {
        const key = localStorage.key(i);
        if (!key || key.indexOf(LOCAL_VENDOR_POSTS_PREFIX) !== 0) {
          continue;
        }
        const posts = getLocalJson(key, []);
        if (!Array.isArray(posts)) {
          continue;
        }
        const filtered = posts.filter((post) => post.id !== postId);
        if (filtered.length !== posts.length) {
          setLocalJson(key, filtered);
          break;
        }
      }
    } catch (error) {
      // localStorage unavailable
    }

    console.log('Delete vendor social post (saved to localStorage)', {
      postId,
    });
    resolve(true);

    // db.collection(POST_COLLECTION)
    //   .doc(postId)
    //   .delete()
    //   .then(() => {
    //     resolve(true);
    //   })
    //   .catch((error) => {
    //     reject(error);
    //   });
  });
}

function normalizePromo(doc) {
  const data = doc.data();
  return {
    id: doc.id,
    title: data.title || 'Untitled Promotion',
    description: data.description || '',
    image: getPromoImage(data),
    sourceType: 'promotion',
  };
}

function normalizeEvent(doc) {
  const data = doc.data();
  return {
    id: doc.id,
    title: data.title || 'Untitled Event',
    description: data.description || data.teaser || '',
    image: getEventImage(data),
    sourceType: 'event',
  };
}

function normalizeLocation(doc) {
  const data = doc.data();
  return {
    id: doc.id,
    title: data.locationTitle || 'Untitled Location',
    description: data.locationDescription || '',
    image: getLocationImage(data),
    sourceType: 'location',
  };
}

export function getVendorSourceContent(vendorId, contentType) {
  const db = firebase.firestore();

  if (contentType === 'promotion') {
    const table = `participants.${vendorId}.access`;
    return new Promise((resolve, reject) => {
      db.collection('promos')
        .where(table, '==', true)
        .get()
        .then((snapshot) => {
          const items = [];
          snapshot.forEach((doc) => {
            if (doc && doc.exists) {
              items.push(normalizePromo(doc));
            }
          });
          resolve(items);
        })
        .catch((error) => reject(error));
    });
  }

  if (contentType === 'event') {
    return new Promise((resolve, reject) => {
      db.collection('events')
        .where('creatorInfo.vendorId', '==', vendorId)
        .get()
        .then((snapshot) => {
          const items = [];
          snapshot.forEach((doc) => {
            if (doc && doc.exists) {
              items.push(normalizeEvent(doc));
            }
          });
          resolve(items);
        })
        .catch((error) => reject(error));
    });
  }

  if (contentType === 'location') {
    return new Promise((resolve, reject) => {
      db.collection('locations')
        .where('venId', '==', vendorId)
        .get()
        .then((snapshot) => {
          const items = [];
          snapshot.forEach((doc) => {
            if (doc && doc.exists) {
              items.push(normalizeLocation(doc));
            }
          });
          resolve(items);
        })
        .catch((error) => reject(error));
    });
  }

  return Promise.resolve([]);
}
